import { CollectionButton, CollectionButtonProps } from '@buggy/shared'
import { Tooltip } from 'antd'
import { FunctionComponent, RefObject, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import AddPostToCollectionModal, {
  AddPostToCollectionModalProps,
} from '@/components/modal/add-post-to-collection-modal'
import { useCollectionActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Nullable } from '@/types/nullable'

interface AddPostToCollectionButtonProps
  extends Pick<CollectionButtonProps, 'type'> {
  post: AddPostToCollectionModalProps['post']
  kol: AddPostToCollectionModalProps['kol']
  from: AddPostToCollectionModalProps['from']
  containerRef?: 'parent' | Nullable<RefObject<HTMLDivElement>>
  onCollectionStatusUpdate?: (postId: string, isCollected: boolean) => void
  isDemo?: boolean
}

const AddPostToCollectionButton: FunctionComponent<
  AddPostToCollectionButtonProps
> = ({
  post,
  kol,
  from,
  containerRef = 'parent',
  onCollectionStatusUpdate,
  type,
  isDemo,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const collectionPermission = useCollectionActionPermission()
  const triggerElementRef = useRef<HTMLDivElement>(null)

  const [isCollectionPost, setIsCollectionPost] = useState(
    post.isCollectionPost,
  )
  const [addPostToCollectionModalOpen, setAddPostToCollectionModalOpen] =
    useState(false)

  const disabled = !collectionPermission || isDemo
  const tooltipTitle: string = useMemo(() => {
    if (!collectionPermission) {
      return formatMessage({
        id: 'workspace:insufficient_permission_tooltip',
      })
    }
    return formatMessage({
      id: isCollectionPost
        ? 'general:is_collected'
        : 'general:add_post_to_collection',
    })
  }, [collectionPermission, formatMessage, isCollectionPost])

  return (
    <div
      ref={triggerElementRef}
      onClick={(event): void => {
        event.stopPropagation()
      }}
    >
      <Tooltip placement='bottom' title={tooltipTitle}>
        <TriggerElement
          onClick={(): void => {
            if (disabled) {
              return
            }
            setAddPostToCollectionModalOpen(true)
          }}
        >
          <CollectionButton
            activeColor={theme.colors.brand.primary}
            collected={isCollectionPost}
            disabled={disabled}
            type={type}
          />
        </TriggerElement>
      </Tooltip>
      <AddPostToCollectionModal
        containerRef={
          containerRef === 'parent'
            ? triggerElementRef
            : (containerRef ?? undefined)
        }
        from={from}
        kol={kol}
        open={addPostToCollectionModalOpen}
        post={post}
        onCancel={(): void => {
          setAddPostToCollectionModalOpen(false)
        }}
        onCollectedChange={(changedKolId, isCollected): void => {
          if (kol?.id !== changedKolId) {
            return
          }

          setIsCollectionPost(isCollected)
          onCollectionStatusUpdate?.(post.id, isCollected)
        }}
      />
    </div>
  )
}

export const TriggerElement = styled.div``

export default AddPostToCollectionButton
